/**
 * Clinical Update Modals
 * ======================
 * Modals for punctual clinical updates (treatment evolution, disease progression, events)
 * Used by IDE and Doctor to record changes without full re-evaluation
 */

import { useState } from "react";
import { X, AlertTriangle, Activity, Pill, TrendingUp, Calendar, Building2, Loader2 } from "lucide-react";
import api from "@/api";
import { useTreatmentReferentials } from "@/components/shared/OncologyContext";
import { toast } from "sonner";
import MedicalDecisionTaskModal from "@/components/shared/MedicalDecisionTaskModal";
import ModalShell from "@/components/shared/ModalShell";
import { DECISION_SOURCES } from "@/lib/medicalDecisionTemplates";

// ============================================
// Treatment Evolution Modal
// ============================================
export function TreatmentEvolutionModal({ patientId, currentData, onClose, onUpdated }) {
  const [loading, setLoading] = useState(false);
  const { therapeuticLines: LINES } = useTreatmentReferentials();
  const [form, setForm] = useState({
    updateType: "",
    previousValue: currentData?.therapeuticLine || "",
    newTherapeuticLine: "",
    newMolecule: "",
    newProtocol: "",
    effectiveDate: new Date().toISOString().split("T")[0],
    reasonCode: "",
    toxicityType: "",
    reason: "",
    doseModificationType: "",
    doseModificationMolecule: "",
    previousDose: "",
    newDose: "",
    doseModificationCause: "",
    doseModificationCycle: "",
  });

  const UPDATE_TYPES = [
    { value: "line_change", label: "Changement de ligne thérapeutique" },
    { value: "molecule_change", label: "Changement de molécule" },
    { value: "protocol_change", label: "Changement de protocole" },
    { value: "suspension", label: "Suspension du traitement" },
    { value: "resumption", label: "Reprise du traitement" },
    { value: "stop", label: "Arrêt définitif du traitement" },
    { value: "dose_modification", label: "Dose modifiée" },
  ];

  const REASON_CODES = ["Progression", "Toxicité", "Réponse insuffisante", "Fin prévue", "Choix patient", "Comorbidité", "Accès médicament / rupture", "Décision RCP", "Autre"];
  const TOXICITY_TYPES = ["Hématologique", "Digestive", "Cutanée", "Neurologique", "Hépatique", "Rénale", "Cardiaque", "Infectieuse", "Autre"];
  const DOSE_MODIFICATION_TYPES = ["Réduction", "Augmentation", "Report", "Reprise dose pleine"];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.updateType) {
      toast.error("Veuillez sélectionner le type de modification");
      return;
    }
    if (form.updateType === "dose_modification" && (!form.doseModificationType || !form.doseModificationMolecule)) {
      toast.error("Veuillez préciser le type de modification et la molécule");
      return;
    }

    setLoading(true);
    try {
      // Get current evaluation
      const evalRes = await api.get(`/clinical/patients/${patientId}/evaluation`);
      if (!evalRes.data.exists) {
        toast.error("Évaluation clinique non trouvée");
        return;
      }

      const currentEval = evalRes.data.evaluation;
      const updatePayload = { ...currentEval };

      // Apply changes based on update type
      if (form.updateType === "line_change") {
        updatePayload.therapeuticLine = form.newTherapeuticLine;
      } else if (form.updateType === "molecule_change") {
        updatePayload.molecules = form.newMolecule ? [form.newMolecule] : (updatePayload.molecules || []);
      } else if (form.updateType === "protocol_change") {
        updatePayload.protocol = form.newProtocol;
      } else if (form.updateType === "suspension") {
        updatePayload.treatmentStatus = "Suspendu";
      } else if (form.updateType === "resumption") {
        updatePayload.treatmentStatus = "En cours";
      } else if (form.updateType === "stop") {
        updatePayload.treatmentStatus = "Arrêté";
      }

      updatePayload.treatmentUpdateType = form.updateType;
      updatePayload.treatmentChangeReasonCode = form.reasonCode || form.doseModificationCause || null;
      updatePayload.treatmentChangeReason = form.reason || null;
      updatePayload.toxicityType = form.toxicityType || null;
      updatePayload.effectiveDate = form.effectiveDate;
      updatePayload.doseModificationType = form.doseModificationType || null;
      updatePayload.doseModificationMolecule = form.doseModificationMolecule || null;
      updatePayload.previousDose = form.previousDose || null;
      updatePayload.newDose = form.newDose || null;
      updatePayload.doseModificationCause = form.doseModificationCause || null;
      updatePayload.doseModificationCycle = form.doseModificationCycle || null;

      await api.put(`/clinical/patients/${patientId}/evaluation`, updatePayload);
      toast.success("Évolution thérapeutique enregistrée");
      onUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalShell testId="treatment-evolution-modal">
        <div className="p-5 border-b bg-blue-50 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <Pill className="w-5 h-5 text-blue-600" />
              Évolution thérapeutique
            </h2>
            <button onClick={onClose} className="p-2 hover:bg-white/50 rounded-lg">
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Type de modification *</label>
            <select
              value={form.updateType}
              onChange={e => setForm(f => ({ ...f, updateType: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
              data-testid="update-type-select"
            >
              <option value="">Sélectionner...</option>
              {UPDATE_TYPES.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>

          {form.updateType === "line_change" && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Nouvelle ligne thérapeutique *</label>
              <select
                value={form.newTherapeuticLine}
                onChange={e => setForm(f => ({ ...f, newTherapeuticLine: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                data-testid="new-line-select"
              >
                <option value="">Sélectionner...</option>
                {LINES.map(l => (
                  <option key={l} value={l}>{l}</option>
                ))}
              </select>
              <p className="text-xs text-slate-500 mt-1">Ligne actuelle: {currentData?.therapeuticLine || "Non définie"}</p>
            </div>
          )}

          {form.updateType === "molecule_change" && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Nouvelle molécule *</label>
              <input
                type="text"
                value={form.newMolecule}
                onChange={e => setForm(f => ({ ...f, newMolecule: e.target.value }))}
                placeholder="Ex: Pembrolizumab"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                data-testid="new-molecule-input"
              />
              <p className="text-xs text-slate-500 mt-1">Molécule actuelle: {currentData?.molecule || "Non définie"}</p>
            </div>
          )}

          {form.updateType === "protocol_change" && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Nouveau protocole *</label>
              <input
                type="text"
                value={form.newProtocol}
                onChange={e => setForm(f => ({ ...f, newProtocol: e.target.value }))}
                placeholder="Ex: FOLFIRI"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                data-testid="new-protocol-input"
              />
              <p className="text-xs text-slate-500 mt-1">Protocole actuel: {currentData?.protocol || "Non défini"}</p>
            </div>
          )}

          {form.updateType === "dose_modification" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Type *</label>
                <select
                  value={form.doseModificationType}
                  onChange={e => setForm(f => ({ ...f, doseModificationType: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  data-testid="dose-modification-type-select"
                >
                  <option value="">Sélectionner...</option>
                  {DOSE_MODIFICATION_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Molécule *</label>
                <input
                  type="text"
                  value={form.doseModificationMolecule}
                  onChange={e => setForm(f => ({ ...f, doseModificationMolecule: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  data-testid="dose-modification-molecule-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Ancienne dose</label>
                <input
                  type="text"
                  value={form.previousDose}
                  onChange={e => setForm(f => ({ ...f, previousDose: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  data-testid="previous-dose-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Nouvelle dose</label>
                <input
                  type="text"
                  value={form.newDose}
                  onChange={e => setForm(f => ({ ...f, newDose: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  data-testid="new-dose-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Cause</label>
                <select
                  value={form.doseModificationCause}
                  onChange={e => setForm(f => ({ ...f, doseModificationCause: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  data-testid="dose-modification-cause-select"
                >
                  <option value="">Sélectionner...</option>
                  {REASON_CODES.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Cycle</label>
                <input
                  type="text"
                  value={form.doseModificationCycle}
                  onChange={e => setForm(f => ({ ...f, doseModificationCycle: e.target.value }))}
                  placeholder="Ex: C3"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  data-testid="dose-modification-cycle-input"
                />
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Date effective</label>
            <input
              type="date"
              value={form.effectiveDate}
              onChange={e => setForm(f => ({ ...f, effectiveDate: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
              data-testid="effective-date-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Motif principal</label>
            <select
              value={form.reasonCode}
              onChange={e => setForm(f => ({ ...f, reasonCode: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
              data-testid="treatment-change-reason-code-select"
            >
              <option value="">Sélectionner...</option>
              {REASON_CODES.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>

          {(form.reasonCode === "Toxicité" || form.doseModificationCause === "Toxicité") && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Type de toxicité</label>
              <select
                value={form.toxicityType}
                onChange={e => setForm(f => ({ ...f, toxicityType: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                data-testid="toxicity-type-select"
              >
                <option value="">Sélectionner...</option>
                {TOXICITY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Motif (optionnel)</label>
            <textarea
              value={form.reason}
              onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
              placeholder="Raison du changement..."
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none h-20"
              data-testid="reason-textarea"
            />
          </div>

          <div className="flex gap-3 pt-3 border-t">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 border border-slate-200 rounded-xl text-slate-600 font-medium">
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              data-testid="submit-treatment-evolution"
              className="flex-1 py-2.5 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Enregistrer
            </button>
          </div>
        </form>
    </ModalShell>
  );
}


// ============================================
// Disease Progression Modal
// ============================================
export function DiseaseProgressionModal({ patientId, currentData, onClose, onUpdated }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    responseStatus: "",
    evaluationDate: new Date().toISOString().split("T")[0],
    evaluationSource: "",
    responseCriteria: "",
    validationLevel: "",
    notes: "",
  });

  const RESPONSE_STATUSES = [
    "Réponse complète",
    "Réponse partielle",
    "Stabilité",
    "Progression",
    "Non évaluée",
  ];

  const SOURCES = ["Clinique", "Imagerie", "Biologie", "Mixte"];
  const RESPONSE_CRITERIA = ["RECIST 1.1", "iRECIST", "Lugano", "PSA/biologique", "Clinique", "Non précisé"];
  const VALIDATION_LEVELS = ["Médecin", "RCP", "Document externe", "Non précisé"];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.responseStatus || !form.evaluationSource) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }

    setLoading(true);
    try {
      await api.post(`/clinical/patients/${patientId}/response`, {
        responseStatus: form.responseStatus,
        evaluationDate: form.evaluationDate,
        evaluationSource: form.evaluationSource,
        responseCriteria: form.responseCriteria || null,
        validationLevel: form.validationLevel || null,
        notes: form.notes || null,
      });
      toast.success("Évaluation de la réponse enregistrée");
      onUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalShell testId="disease-progression-modal">
        <div className="p-5 border-b bg-purple-50 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-purple-600" />
              Évaluation de la réponse
            </h2>
            <button onClick={onClose} className="p-2 hover:bg-white/50 rounded-lg">
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Statut de réponse *</label>
            <div className="grid grid-cols-2 gap-2">
              {RESPONSE_STATUSES.map(status => (
                <button
                  key={status}
                  type="button"
                  onClick={() => setForm(f => ({ ...f, responseStatus: status }))}
                  className={`py-2 px-3 rounded-lg text-sm font-medium border-2 transition-all ${
                    form.responseStatus === status
                      ? status === "Progression" ? "bg-red-100 border-red-400 text-red-700" :
                        status === "Réponse complète" ? "bg-green-100 border-green-400 text-green-700" :
                        "bg-purple-100 border-purple-400 text-purple-700"
                      : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
                  }`}
                  data-testid={`response-status-${status}`}
                >
                  {status}
                </button>
              ))}
            </div>
            {currentData?.currentResponse && (
              <p className="text-xs text-slate-500 mt-2">Réponse actuelle: {currentData.currentResponse}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Date d'évaluation *</label>
            <input
              type="date"
              value={form.evaluationDate}
              onChange={e => setForm(f => ({ ...f, evaluationDate: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
              data-testid="eval-date-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Source de l'évaluation *</label>
            <div className="flex gap-2">
              {SOURCES.map(src => (
                <button
                  key={src}
                  type="button"
                  onClick={() => setForm(f => ({ ...f, evaluationSource: src }))}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium border-2 transition-all ${
                    form.evaluationSource === src
                      ? "bg-purple-100 border-purple-400 text-purple-700"
                      : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
                  }`}
                  data-testid={`source-${src}`}
                >
                  {src}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Critère</label>
              <select
                value={form.responseCriteria}
                onChange={e => setForm(f => ({ ...f, responseCriteria: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                data-testid="response-criteria-select"
              >
                <option value="">Sélectionner...</option>
                {RESPONSE_CRITERIA.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Validation</label>
              <select
                value={form.validationLevel}
                onChange={e => setForm(f => ({ ...f, validationLevel: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                data-testid="response-validation-level-select"
              >
                <option value="">Sélectionner...</option>
                {VALIDATION_LEVELS.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Notes (optionnel)</label>
            <textarea
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="Détails de l'évaluation..."
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none h-20"
              data-testid="notes-textarea"
            />
          </div>

          <div className="flex gap-3 pt-3 border-t">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 border border-slate-200 rounded-xl text-slate-600 font-medium">
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              data-testid="submit-response-eval"
              className="flex-1 py-2.5 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Enregistrer
            </button>
          </div>
        </form>
    </ModalShell>
  );
}


// ============================================
// Clinical Event Modal (ER Visit, Hospitalization)
// ============================================
export function ClinicalEventModal({ patientId, patientName, onClose, onUpdated }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    eventType: "",
    visitDate: new Date().toISOString().split("T")[0],
    startDate: new Date().toISOString().split("T")[0],
    endDate: "",
    reason: "",
    reasonDetail: "",
    outcome: "",
    service: "",
    treatmentRelated: "Inconnu",
    causalityLevel: "Inconnu",
    suspectedMolecule: "",
    suspectedProtocol: "",
  });
  const [decisionOpen, setDecisionOpen] = useState(false);
  const [decisionCtx, setDecisionCtx] = useState(null);

  const EVENT_TYPES = [
    { value: "er_visit", label: "Passage aux urgences", icon: AlertTriangle, color: "text-orange-600" },
    { value: "hospitalization", label: "Hospitalisation", icon: Building2, color: "text-red-600" },
    { value: "consultation", label: "Consultation non programmée", icon: Activity, color: "text-blue-600" },
    { value: "death", label: "Décès", icon: X, color: "text-slate-600" },
  ];

  const REASONS = ["Toxicité", "Douleur", "Infection", "Déshydratation", "Progression", "Décompensation", "Fièvre", "AEG", "Autre"];
  const OUTCOMES = ["Retour domicile", "Hospitalisation", "Observation courte", "Transfert", "Autre"];
  const SERVICES = ["Oncologie", "Urgences", "Réanimation", "Médecine interne", "Autre"];
  const TREATMENT_RELATED_OPTIONS = ["Oui", "Possible", "Non", "Inconnu"];

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Décès n'a pas besoin de motif
    if (!form.eventType || (form.eventType !== "death" && !form.reason)) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }

    setLoading(true);
    try {
      let eventData;
      
      if (form.eventType === "er_visit") {
        eventData = {
          visitDate: form.visitDate,
          reason: form.reason,
          reasonDetail: form.reasonDetail || null,
          outcome: form.outcome || null,
          treatmentRelated: form.treatmentRelated,
          causalityLevel: form.causalityLevel,
          suspectedMolecule: form.suspectedMolecule || null,
          suspectedProtocol: form.suspectedProtocol || null,
        };
      } else if (form.eventType === "hospitalization") {
        eventData = {
          startDate: form.startDate,
          reason: form.reason,
          reasonDetail: form.reasonDetail || null,
          service: form.service || null,
          treatmentRelated: form.treatmentRelated,
          causalityLevel: form.causalityLevel,
          suspectedMolecule: form.suspectedMolecule || null,
          suspectedProtocol: form.suspectedProtocol || null,
        };
      } else if (form.eventType === "death") {
        eventData = {
          deathDate: form.visitDate,
        };
      } else {
        eventData = {
          date: form.visitDate,
          reason: form.reason,
          reasonDetail: form.reasonDetail || null,
          treatmentRelated: form.treatmentRelated,
          causalityLevel: form.causalityLevel,
          suspectedMolecule: form.suspectedMolecule || null,
          suspectedProtocol: form.suspectedProtocol || null,
        };
      }

      const response = await api.post(`/clinical/patients/${patientId}/events`, {
        eventType: form.eventType,
        eventData,
      });
      
      // Show special message for hospitalization or death
      if (form.eventType === "hospitalization") {
        toast.success("Hospitalisation enregistrée - Le statut du patient a été mis à jour", {
          description: "Vous pourrez ajouter la date de sortie dans l'onglet Historique"
        });
      } else if (form.eventType === "death") {
        toast.success("Décès enregistré - Le dossier patient a été clôturé");
      } else {
        toast.success("Événement clinique enregistré");
      }

      // Phase 2 — proposer une mission SEC pour hospitalization/er_visit
      const eventId = response?.data?.eventId;
      if (form.eventType === "hospitalization") {
        setDecisionCtx({
          decisionSource: "EVENT_HOSPITALIZATION",
          reason: [form.reason, form.reasonDetail].filter(Boolean).join(" — "),
          sourceRefs: { eventId },
        });
        setDecisionOpen(true);
        return;
      }
      if (form.eventType === "er_visit") {
        setDecisionCtx({
          decisionSource: "EVENT_ER_VISIT",
          reason: [form.reason, form.reasonDetail].filter(Boolean).join(" — "),
          sourceRefs: { eventId },
        });
        setDecisionOpen(true);
        return;
      }

      onUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalShell
      testId="clinical-event-modal"
      after={(
        <MedicalDecisionTaskModal
          open={decisionOpen}
          onClose={() => { setDecisionOpen(false); onUpdated(); }}
          onDone={() => { setDecisionOpen(false); onUpdated(); }}
          decisionSource={decisionCtx?.decisionSource}
          patientId={patientId}
          patientName={patientName}
          context={{ reason: decisionCtx?.reason }}
          sourceRefs={decisionCtx?.sourceRefs}
        />
      )}
    >
        <div className="p-5 border-b bg-red-50 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              Événement clinique
            </h2>
            <button onClick={onClose} className="p-2 hover:bg-white/50 rounded-lg">
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Type d'événement *</label>
            <div className="grid grid-cols-2 gap-2">
              {EVENT_TYPES.map(et => (
                <button
                  key={et.value}
                  type="button"
                  onClick={() => setForm(f => ({ ...f, eventType: et.value }))}
                  className={`py-3 px-3 rounded-xl text-sm font-medium border-2 transition-all flex flex-col items-center gap-1 ${
                    form.eventType === et.value
                      ? "bg-red-100 border-red-400 text-red-700"
                      : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
                  }`}
                  data-testid={`event-type-${et.value}`}
                >
                  <et.icon className={`w-5 h-5 ${form.eventType === et.value ? "text-red-600" : "text-slate-400"}`} />
                  {et.label}
                </button>
              ))}
            </div>
          </div>

          {form.eventType === "er_visit" && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Date du passage</label>
              <input
                type="date"
                value={form.visitDate}
                onChange={e => setForm(f => ({ ...f, visitDate: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                data-testid="visit-date-input"
              />
            </div>
          )}

          {form.eventType === "hospitalization" && (
            <>
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                <strong>Note :</strong> L'enregistrement d'une hospitalisation changera automatiquement le statut du patient en "Hospitalisé". Vous pourrez ajouter la date de sortie ultérieurement depuis l'onglet Historique.
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Date d'entrée</label>
                  <input
                    type="date"
                    value={form.startDate}
                    onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    data-testid="start-date-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Service</label>
                  <select
                    value={form.service}
                    onChange={e => setForm(f => ({ ...f, service: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    data-testid="service-select"
                  >
                    <option value="">Sélectionner...</option>
                    {SERVICES.map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              </div>
            </>
          )}

          {(form.eventType === "consultation") && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Date</label>
              <input
                type="date"
                value={form.visitDate}
                onChange={e => setForm(f => ({ ...f, visitDate: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                data-testid="event-date-input"
              />
            </div>
          )}

          {/* Décès - seulement la date */}
          {form.eventType === "death" && (
            <>
              <div className="p-3 bg-slate-100 border border-slate-300 rounded-lg text-sm text-slate-700">
                <strong>Attention :</strong> L'enregistrement d'un décès clôturera définitivement le dossier du patient.
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Date du décès</label>
                <input
                  type="date"
                  value={form.visitDate}
                  onChange={e => setForm(f => ({ ...f, visitDate: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  data-testid="death-date-input"
                />
              </div>
            </>
          )}

          {/* Motif - pas pour décès */}
          {form.eventType && form.eventType !== "death" && (
            <>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Motif *</label>
                <select
                  value={form.reason}
                  onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  data-testid="reason-select"
                >
                  <option value="">Sélectionner...</option>
                  {REASONS.map(r => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Détails (optionnel)</label>
                <input
                  type="text"
                  value={form.reasonDetail}
                  onChange={e => setForm(f => ({ ...f, reasonDetail: e.target.value }))}
                  placeholder="Précisions sur le motif..."
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  data-testid="reason-detail-input"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Lié au traitement ?</label>
                  <select
                    value={form.treatmentRelated}
                    onChange={e => setForm(f => ({ ...f, treatmentRelated: e.target.value, causalityLevel: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    data-testid="event-treatment-related-select"
                  >
                    {TREATMENT_RELATED_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Molécule suspectée</label>
                  <input
                    type="text"
                    value={form.suspectedMolecule}
                    onChange={e => setForm(f => ({ ...f, suspectedMolecule: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    data-testid="event-suspected-molecule-input"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-2">Protocole suspecté</label>
                  <input
                    type="text"
                    value={form.suspectedProtocol}
                    onChange={e => setForm(f => ({ ...f, suspectedProtocol: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    data-testid="event-suspected-protocol-input"
                  />
                </div>
              </div>
            </>
          )}

          {form.eventType === "er_visit" && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Issue</label>
              <select
                value={form.outcome}
                onChange={e => setForm(f => ({ ...f, outcome: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                data-testid="outcome-select"
              >
                <option value="">Sélectionner...</option>
                {OUTCOMES.map(o => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </div>
          )}

          <div className="flex gap-3 pt-3 border-t">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 border border-slate-200 rounded-xl text-slate-600 font-medium">
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              data-testid="submit-clinical-event"
              className="flex-1 py-2.5 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Enregistrer
            </button>
          </div>
        </form>
    </ModalShell>
  );
}
